import React, { FC, useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';
import {
  Header,
  HeaderExpandableMenuItem,
  HeaderMenuItem,
  HeaderMenuItemChild,
  MOBILE_MEDIA_QUERY,
} from '@saucelabs/ds-navigation';
import {
  IconBellOutline,
  IconExternalOutline,
  IconKeyOutline,
  IconQuestionOutline,
  IconSearchOutline,
  IconSignoutOutline,
  IconUserOutline,
} from '@saucelabs/ds-icons';
import { DropdownItem, DropdownItemDivider } from '@saucelabs/ds-dropdown';
import { records, selectors as userSelectors } from '@saucelabs/sl-module-user';
import { actions as usageActions } from '@saucelabs/sl-module-usage';
import { UsageModuleState } from '@saucelabs/sl-module-usage/lib/reducers';
import config from '@saucelabs/sl-util-config';
import { prettyDCName, useMediaQuery } from '@saucelabs/sl-util-helpers';
import { operations as authOperations } from '@saucelabs/sl-container-authentication';
import { useFlags } from '@saucelabs/sl-util-launchdarkly';
import { CopyText } from '@saucelabs/ds-copy-text';
import { recordTrack, STANDARD_EVENTS, trackStandardEvent } from '@saucelabs/sl-util-segmentio';
import {
  constants as features,
  isSupportedFeature,
} from '@saucelabs/sl-component-supported-feature';
import { Typography } from '@saucelabs/ds-typography';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { SearchModal } from '@saucelabs/sl-component-search-modal';
import { USER_TYPE_FREE_TRIAL } from '@saucelabs/sl-module-user/lib/constants';

import { NAVIGATION_EVENTS, V2_VERSION } from '../tracking';
import { actions } from '../ducks/dashboard-container-new';
import { DashboardContainerNewState } from '../ducks/dashboard-container-new/reducers';
import { switchActiveTeam as operationSwitchActiveTeam } from '../../application/operations';
import { HeaderItemType, isActiveItem, useActiveDatacenterList, useFocusTarget } from '../utils';
import { OneClickBillingCta } from './OneClickBilling';
import InviteUserButton from './InviteUserButton';
import { HeaderLogo } from './HeaderLogo';
import { HeaderVerticalDivider } from './HeaderVerticalDivider';
import { SKIP_MENU_IDS } from './SkipMenu';
import { LOCAL_STORAGE_KEY_ACTIVE_TEAM } from '../../constants';

import { haveAccessToMultipleTeams } from '@saucelabs/web-ui-team-management/src/utils';

const { statusPageUrl, CURRENT_DC, dataCentersList, MULTI_DC_SUPPORT, adminUiUrl } = config;

declare global {
  interface Window {
    Beamer: {
      observing: boolean;
      init(): void;
    };
  }
}

export const HeaderNew: FC<HeaderNewProps> = (props) => {
  const {
    className,
    handleLogout,
    isCollapsed,
    isExpiredUser,
    isFreemiumCapable,
    isMobileCollapsed,
    isOnFreeTrialPlan,
    switchActiveTeam,
    switchDCCallback,
    toggleCollapsed,
    toggleMobileCollapsed,
    user,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    usageUnits,
    freemiumRefillDate,
    fetchNextFreemiumReplenishment,
    fetchUser,
  } = props;
  if (window.Beamer && !window.Beamer.observing) {
    window.Beamer.init();
  }

  const dataCenters = useActiveDatacenterList(dataCentersList);
  const location = useLocation();
  const { matches: isMobile } = useMediaQuery(MOBILE_MEDIA_QUERY);
  const disabledDataCenters = user && dataCenters ? (
    user?.getDisabledRegions(dataCenters)
  ) : [];

  const hasBillingAccess = (
    isSupportedFeature(features.BILLING) && user?.canSelfServe() && user?.isOrgAdmin()
  );

  const username = user?.get('username');
  const accessKey = user?.get('access_key') ?? '';
  const accessKeyLastDigits = accessKey.slice(-4);
  const visibleKey = new Array(accessKey.length ? accessKey.length - 4 : 0).fill('*').join('') + accessKeyLastDigits;
  const isFreeUserAndOrgAdmin = (user?.isFreeUser() || isFreemiumCapable) &&
    user?.isOrgAdmin();

  const checkActiveTeamChangeLocalStorage = useCallback(() => {
    // Not logged in user can access public tests, hence optional chaining
    const currentTabActiveTeam = user?.teams?.[0]?.id;
    if (
      user?.teams?.length &&
      localStorage.getItem(LOCAL_STORAGE_KEY_ACTIVE_TEAM) !== currentTabActiveTeam
    ) {
      fetchUser();
    }
  }, [user, fetchUser]);

  useEffect(() => {
    window.addEventListener('storage', checkActiveTeamChangeLocalStorage);

    return () => {
      window.removeEventListener('storage', checkActiveTeamChangeLocalStorage);
    };
  }, [checkActiveTeamChangeLocalStorage]);

  useEffect(() => {
    // If user is on freemium, fetch their refill date if it's not in the state already
    if (isFreemiumCapable && !freemiumRefillDate && user?.organization) {
      fetchNextFreemiumReplenishment(user.organization.id);
    }
  }, [fetchNextFreemiumReplenishment, freemiumRefillDate, user]);

  // check is SSO enabled
  const isSSOEnabled = user?.getOrgSettings()?.sso_enabled;

  // make search visible or invisible
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const flags = useFlags();
  const { className: focusClasses, ...otherFocusProps } = useFocusTarget(SKIP_MENU_IDS.HEADER);
  const trialEnd = isOnFreeTrialPlan ? // OneClickBillingCta expects it to be a seconds-precision timestamp
    new Date(user.organization.plans_subscriptions.free_trial_expiry).getTime() / 1000 :
    user?.getTrialEnd();

  return (
    <Header
      appendLogoComponent={haveAccessToMultipleTeams(user) && user?.teams && (
        <HeaderExpandableMenuItem
          data-qa="nav-team-switch"
          label="Switch between teams"
          labelNode={(
            <div className="flex flex-col">
              <HeaderVerticalDivider className="absolute left-0 top-1.5" />
              <Typography
                className="m-0 text-left"
                component="h6"
                variant="body-small"
              >
                Team
              </Typography>
              <Typography
                className="truncate max-w-xs text-left text-high-emphasis"
                component="span"
                variant="body"
              >
                {user.teams[0].name}
              </Typography>
              <HeaderVerticalDivider className="absolute right-0 top-1.5" />
            </div>
          )}
        >
          {user.teams.map(({ id, name }) => (
            <DropdownItem
              key={id}
              isSelectable
              isSelected={user.teams[0].id === id} // first team in the list is the active one
              onClick={() => {
                if (id !== user.teams[0].id) {
                  switchActiveTeam({ team: id });
                }
              }}
            >
              {name}
            </DropdownItem>
          ))}
        </HeaderExpandableMenuItem>
      )}
      aria-label="User & Help"
      className={classnames('flex-shrink-0', className, focusClasses)}
      isCollapsed={isCollapsed}
      isCollapsible={user?.isAuthenticated() ? true : isMobile}
      isMobileCollapsed={isMobileCollapsed}
      logo={<HeaderLogo />}
      onToggleMobileNav={toggleMobileCollapsed}
      onToggleNav={toggleCollapsed}
      {...otherFocusProps}
    >
      {user?.isAuthenticated() && username && accessKey && (
        <HeaderExpandableMenuItem
          data-qa="nav-access-key"
          icon={IconKeyOutline}
          label="Username & Access Key"
          overlayClassNames="w-full max-w-50"
        >
          <DropdownItem
            // Prevent auto-closing of header navigation since these are copytext fields
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            onClick={(ev) => ev.stopPropagation()}
          >
            <CopyText
              className="w-full"
              emphasis="low"
              label="Username"
              onPress={() => {
                trackStandardEvent(
                  STANDARD_EVENTS.OBJECT_CLICKED,
                  'Growth',
                  'Header Username',
                  'Copy Username',
                  {
                    domain: window.location.origin,
                    page: window.location.pathname,
                  },
                );
              }}
              value={username}
            />
          </DropdownItem>
          <DropdownItem
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            onClick={(ev) => ev.stopPropagation()}
          >
            <CopyText
              className="w-full"
              copyValue={accessKey}
              emphasis="low"
              label="Access Key"
              onPress={() => {
                trackStandardEvent(
                  STANDARD_EVENTS.OBJECT_CLICKED,
                  'Growth',
                  'Header Username',
                  'Copy Access Key',
                  {
                    domain: window.location.origin,
                    page: window.location.pathname,
                  },
                );
              }}
              value={visibleKey}
            />
          </DropdownItem>
        </HeaderExpandableMenuItem>
      )}
      {flags.isSearchAvailable && (
        <HeaderMenuItem
          className="search"
          data-qa="nav-search"
          icon={IconSearchOutline}
          label="Search"
          onClick={() => setIsSearchVisible(true)}
        />
      )}
      <HeaderMenuItem
        className="beamer-connect"
        data-qa="nav-announcements"
        icon={IconBellOutline}
        label="Product Announcements"
      />
      <HeaderExpandableMenuItem
        data-qa="nav-help"
        icon={IconQuestionOutline}
        label="Help and Support"
      >
        <HeaderMenuItemChild
          external
          onClick={() => (
            recordTrack(NAVIGATION_EVENTS.VISIT_SUPPORT_PORTAL, { version: V2_VERSION })
          )}
          postfixIcon={IconExternalOutline}
          rel="noopener noreferrer"
          target="_blank"
          to="https://support.saucelabs.com"
        >
          Support Portal
        </HeaderMenuItemChild>
        <HeaderMenuItemChild
          external
          onClick={() => (
            recordTrack(NAVIGATION_EVENTS.VISIT_DOCUMENTATION, { version: V2_VERSION })
          )}
          postfixIcon={IconExternalOutline}
          rel="noopener noreferrer"
          target="_blank"
          to="https://docs.saucelabs.com/"
        >
          Documentation
        </HeaderMenuItemChild>
        <HeaderMenuItemChild
          external
          onClick={() => recordTrack(
            NAVIGATION_EVENTS.VISIT_STATUS_PAGE,
            { version: V2_VERSION },
          )}
          postfixIcon={IconExternalOutline}
          rel="noopener noreferrer"
          target="_blank"
          to={statusPageUrl}
        >
          Status Page
        </HeaderMenuItemChild>
      </HeaderExpandableMenuItem>
      {user?.isAuthenticated() && (
        <HeaderExpandableMenuItem
          data-qa="nav-account"
          icon={IconUserOutline}
          isChildCurrent={isActiveItem(location, HeaderItemType.Account)}
          label="Account"
        >
          {user?.isAuthenticated() && (
            <>
              <div
                className="py-1 px-2 truncate"
                role="presentation"
              >
                <span className="inline-block w-full truncate">
                  <Tooltip
                    delay={500}
                    isOpen={user?.get<string>('username')?.length > 20 ? undefined : false}
                    title={user?.get('username')}
                  >
                    <Typography
                      className="text-high-emphasis font-semibold"
                      component="span"
                      variant="body"
                    >
                      {user?.get('username')}
                    </Typography>
                  </Tooltip>
                  <br />
                  <Tooltip
                    delay={500}
                    isOpen={user?.get<string>('email')?.length > 20 ? undefined : false}
                    title={user?.get('email')}
                  >
                    <Typography
                      className="text-medium-emphasis"
                      component="span"
                      variant="body-small"
                    >
                      {user?.get('email')}
                    </Typography>
                  </Tooltip>
                </span>
              </div>
              <DropdownItemDivider />
            </>
          )}
          {user?.isAdmin() && (
            <>
              <HeaderMenuItemChild
                external
                postfixIcon={IconExternalOutline}
                rel="noopener noreferrer"
                target="_blank"
                to={adminUiUrl}
              >
                Sauce Admin
              </HeaderMenuItemChild>
              <DropdownItemDivider />
            </>
          )}
          {user?.isOrgAdmin() && (
            <HeaderMenuItemChild
              to="/team-management"
            >
              Team Management
            </HeaderMenuItemChild>
          )}
          {!user.isOrgAdmin() && user.isTeamAdmin() && (
            <HeaderMenuItemChild
              to={`/team-management/teams/${user?.getUserTeamId() ?? ''}`}
            >
              Team Management
            </HeaderMenuItemChild>
          )}
          {hasBillingAccess && (
            <HeaderMenuItemChild
              to="/billing"
            >
              Subscription and Billing
            </HeaderMenuItemChild>
          )}
          <HeaderMenuItemChild
            to="/user-settings"
          >
            User Settings
          </HeaderMenuItemChild>
          {isSupportedFeature(features.INTEGRATIONS) && user.get('allow_integrations_page') && (
            <HeaderMenuItemChild
              to="/integrations"
            >
              Integrations
            </HeaderMenuItemChild>
          )}
          <DropdownItemDivider />
          <HeaderMenuItemChild
            data-qa="header-nav-logout"
            onClick={() => handleLogout?.()}
            postfixIcon={IconSignoutOutline}
          >
            Log Out
          </HeaderMenuItemChild>
        </HeaderExpandableMenuItem>
      )}
      {user?.isOrgAdmin() && (
        <li
          className="flex items-center"
          data-qa="nav-invite"
        >
          <HeaderVerticalDivider />
          { !isSSOEnabled && <InviteUserButton /> }
        </li>
      )}
      {CURRENT_DC && user?.isAuthenticated() && MULTI_DC_SUPPORT && (
        <HeaderExpandableMenuItem
          className="relative"
          data-qa="nav-dc"
          label="Data Center"
          labelNode={(
            <>
              <HeaderVerticalDivider className="absolute left-0 top-1.5" />
              <span className="text-high-emphasis">{prettyDCName(CURRENT_DC)}</span>
            </>
          )}
        >
          {dataCenters?.map(({ value, url, label }) => (
            <DropdownItem
              key={value}
              data-qa={`header-nav-dc-${value}`}
              isDisabled={disabledDataCenters?.includes(value)}
              isSelectable
              isSelected={CURRENT_DC === value}
              onClick={() => {
                if (!disabledDataCenters?.includes(value)) {
                  switchDCCallback({
                    dcName: value,
                    dcUrl: url,
                  });
                }
              }}
            >
              {label}
            </DropdownItem>
          ))}
        </HeaderExpandableMenuItem>
      )}
      {
        // does include free, free-trial and freemium users
        isFreeUserAndOrgAdmin &&
        (
          <li
            className="flex items-center mx-1"
            data-qa="nav-billing-cta"
          >
            <OneClickBillingCta
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              freemiumRefillDate={freemiumRefillDate}
              isExpired={isExpiredUser}
              isFreemiumCapable={isFreemiumCapable}
              isFreemiumOnly={user?.isFreemiumUser()}
              isOldFreeTrial={user?.getOrgUserType() === USER_TYPE_FREE_TRIAL}
              isOnFreeTrialPlan={isOnFreeTrialPlan}
              manualMinutes={user?.monthly_minutes.manual}
              manualMinutesRemaining={user?.manual_minutes}
              trialEnd={trialEnd}
              usageUnits={usageUnits}
              userId={user?.getUserID()}
              vdcAutomatedMinutes={user?.monthly_minutes.automated}
              vdcAutomatedMinutesRemaining={user?.minutes}
            />
          </li>
        )
      }
      <SearchModal
        indexName="sauce-docs"
        isOpen={isSearchVisible}
        onClose={() => setIsSearchVisible(false)}
      />
    </Header>
  );
};

export interface HeaderNewProps {
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  usageUnits?: UsageUnits;
  disabledDataCenters?: string[];
  fetchNextFreemiumReplenishment: (userOrgId: string) => void;
  fetchUser: () => void;
  freemiumRefillDate?: string | null;
  handleLogout: () => void;
  isCollapsed?: boolean;
  isExpiredUser?: boolean;
  isFreemiumCapable?: boolean;
  isMobileCollapsed?: boolean;
  isOnFreeTrialPlan?: boolean;
  switchActiveTeam: ({ team: string }) => void;
  switchDCCallback: (newDataCenter: { dcName: string; dcUrl: string }) => void;
  toggleCollapsed: () => void;
  toggleMobileCollapsed: () => void;
  user?: records.UserRecord;
}

// eslint-disable-next-line max-len
/* eslint-disable @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call */
const mapStateToProps = (state: {
  user: records.UserRecord,
  dashboard: DashboardContainerNewState,
  usage: UsageModuleState,
}) => ({
  user: state.user,
  usageUnits: state.usage?.usageUnits,
  isCollapsed: state.dashboard.isCollapsed,
  isMobileCollapsed: state.dashboard.isMobileCollapsed,
  isExpiredUser: userSelectors.isExpiredUserSelector(state),
  freemiumRefillDate: state.usage?.nextFreemiumReplenishment,
});

const mapDispatchToProps = {
  fetchNextFreemiumReplenishment: usageActions.getNextFreemiumReplenishment,
  fetchUser: authOperations.getAuthenticatedUser,
  handleLogout: authOperations.logout,
  switchActiveTeam: operationSwitchActiveTeam,
  switchDCCallback: authOperations.switchDC,
  toggleCollapsed: actions.toggleSidebar,
  toggleMobileCollapsed: actions.toggleMobileMenu,
};
// eslint-disable-next-line max-len
/* eslint-enable @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call */

export default connect(mapStateToProps, mapDispatchToProps)(HeaderNew);
